import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { Product as ProductService } from "../../../services/Catalog Management";
import { Product as ProductAdapter } from "../../../adapters/Catalog Management";
import { Warehouse as WarehouseService } from "src/services/Stock Management";

import {
  select as select_characteristic_types,
  select_skus as select_skus_characteristic_types,
} from "../characteristic_type";
import { select as select_categories } from "../category";
import {
  //ready,
  select as select_characteristic_values,
} from "../characteristic_value";

import { addTitleOption, createOptions } from "../../../utils/select";
import {
  clearIdempotencyKey,
  getIdempotencyKey,
  getStockDraftKey,
  isDefinitiveFailure,
} from "../../../utils/idempotency";

const service = new ProductService();
const warehouseService = new WarehouseService();
const adapter = new ProductAdapter();

const REDUCER_MODULE_NAME = "PRODUCT";

const initialState = {
  grid: {
    activos: {
      page: 1,
      skip: 0,
      take: 6,
      rows: 0,
      data: [],
      show: 6,
    },
    inactivos: {
      page: 1,
      skip: 0,
      take: 3,
      rows: 0,
      data: [],
      show: 3,
    },
  },
  stock: [],
  select: [],
  products: [],
  loading: false,
  formIsReady: false,
  dashboard: {
    products: 0,
    skus: 0,
    total_price: 0,
  },
  data: "",
  data_not_adapted: "",
  message: "",
  error: "",
};

const loadingReducer = (state, _) => {
  return {
    ...state,
    formIsReady: false,
    loading: true,
  };
};

export const check_characteristic_values_select = createAsyncThunk(
  `CHECK_CHARACTERISTIC_VALUES_SELECT_${REDUCER_MODULE_NAME}`,
  async ({ characteristics, position }, { dispatch }) => {
    dispatch(
      select_characteristic_values({
        base_url: service.base_url,
        characteristic_type: characteristics[position].characteristic_type_id,
      }),
    );
  },
);

export const grid_activos = createAsyncThunk(
  `GRID_ACTIVOS_${REDUCER_MODULE_NAME}`,
  async ({ skip, take, currentPage = 1 }) => {
    const { data } = await service.grid(skip, take, 1);

    return {
      skip,
      take,
      grid_data: data,
      page: currentPage,
    };
  },
);

export const grid_inactivos = createAsyncThunk(
  `GRID_INACTIVOS_${REDUCER_MODULE_NAME}`,
  async ({ skip, take, currentPage = 1 }) => {
    const { data } = await service.grid(skip, take, 0);

    return {
      skip,
      take,
      grid_data: data,
      page: currentPage,
    };
  },
);

export const select_by_category = createAsyncThunk(
  `SELECT_BY_CATEGORY_${REDUCER_MODULE_NAME}`,
  async (categoryName) => {
    const { data } = await service.selectByCategory(categoryName);

    return {
      select: addTitleOption(createOptions(data, ["skuId", "productName"])),
      products: data.reduce((acum, item) => {
        const { skuId, ...skuData } = item;

        return { ...acum, [skuId]: skuData };
      }, {}),
    };
  },
);

export const get_stock_v1 = createAsyncThunk(
  `STOCK_V1_${REDUCER_MODULE_NAME}`,
  async () => {
    const { data } = await service.stockV1();

    const rows = data.map((item) => ({
      id: item.articulo,
      articulo: item.name,
      stock_management_level: item.stock_management_level || "SKU",
      stock_total: item.stock_total,
      stock_available: item.stock_available,
      carga: 0,
      descarga: 0,
    }));
    let draft = null;
    try {
      draft = JSON.parse(localStorage.getItem(getStockDraftKey()));
    } catch (_) {
      localStorage.removeItem(getStockDraftKey());
    }
    const draftRows = draft?.version === 2 ? draft.rows : null;
    const sameRows =
      Array.isArray(draftRows) &&
      rows.length === draftRows.length &&
      rows.every((row) =>
        draftRows.some(
          (saved) =>
            saved.id === row.id &&
            saved.stock_management_level === row.stock_management_level,
        ),
      );

    if (!sameRows) return rows;

    return rows.map((row) => {
      const saved = draftRows.find((item) => item.id === row.id);
      return {
        ...row,
        carga: isNaN(saved.carga) ? 0 : parseInt(saved.carga),
        descarga: isNaN(saved.descarga) ? 0 : parseInt(saved.descarga),
      };
    });
  },
);

export const changeActive = createAsyncThunk(
  `CHANGE_ACTIVE_${REDUCER_MODULE_NAME}`,
  async ({ id, active }, { dispatch }) => {
    await service.changeActive(id, { active: !active });

    dispatch(
      grid_activos({
        skip: initialState.grid.activos.skip,
        take: initialState.grid.activos.take,
      }),
    );
    dispatch(
      grid_inactivos({
        skip: initialState.grid.inactivos.skip,
        take: initialState.grid.inactivos.take,
      }),
    );
  },
);

export const form = createAsyncThunk(
  `FORM_${REDUCER_MODULE_NAME}`,
  async (id, { dispatch, getState }) => {
    if (id) {
      await dispatch(getById(id));
      const { product } = getState();

      let index = 0;
      for (const variant of product.data.variants) {
        dispatch(
          check_characteristic_values_select({
            characteristics: product.data.variants,
            position: index,
          }),
        );
        index++;
      }
    }

    await Promise.all([
      dispatch(select_categories()),
      dispatch(
        select_characteristic_types({
          base_url: service.base_url,
          leaf: "PRODUCT",
        }),
      ),
      dispatch(
        select_skus_characteristic_types({
          base_url: service.base_url,
          leaf: "SKU",
        }),
      ),
    ]);
  },
);

export const getById = createAsyncThunk(
  `GET_${REDUCER_MODULE_NAME}`,
  async (id) => {
    const entity = await service.getById(id);

    return {
      entity: entity.data,
    };
  },
);

export const dashboard = createAsyncThunk(
  `DASHBOARD_${REDUCER_MODULE_NAME}`,
  async () => {
    const { data: dashboard } = await service.sku();

    return { dashboard };
  },
);

export const submit = createAsyncThunk(
  `SUBIMIT_FORM_${REDUCER_MODULE_NAME}`,
  async ({ id, data }) => {
    const scope = `product:${id || "create"}`;
    let idempotencyOperation = null;
    try {
      const data_to_submit = adapter.from_form_to_api(data);
      const method = id ? "PUT" : "POST";
      const endpoint = id ? `/products/${id}` : "/products";
      idempotencyOperation = {
        scope,
        method,
        endpoint,
        payload: data_to_submit,
      };
      const idempotencyKey = await getIdempotencyKey(idempotencyOperation);

      if (id) {
        await service.update(id, data_to_submit, idempotencyKey);
      } else {
        await service.create(data_to_submit, idempotencyKey);
      }
      await clearIdempotencyKey(scope);

      return {
        error: "",
        message: `Producto ${id ? "modificado" : "creado"} correctamente.`,
      };
    } catch (error) {
      if (isDefinitiveFailure(error) && idempotencyOperation) {
        await clearIdempotencyKey(scope, idempotencyOperation);
      }
      return {
        error: error.message,
        message: "",
      };
    }
  },
);

export const submitStockV1 = createAsyncThunk(
  `SUBIMIT_STOCKV1_FORM_${REDUCER_MODULE_NAME}`,
  async ({ data }, { dispatch }) => {
    const scope = "stock:v1";
    const idempotencyOperation = {
      scope,
      method: "PUT",
      endpoint: "/stock/v1",
      payload: data,
    };
    try {
      const idempotencyKey = await getIdempotencyKey(idempotencyOperation);
      await warehouseService.update(data, idempotencyKey);
      await clearIdempotencyKey(scope);

      localStorage.removeItem(getStockDraftKey());

      return {
        error: "",
        message: `Stock modificado correctamente.`,
      };
    } catch (error) {
      if (isDefinitiveFailure(error)) {
        await clearIdempotencyKey(scope, idempotencyOperation);
      }
      return {
        error: error.message,
        message: "",
      };
    }
  },
);

export const slice = createSlice({
  name: REDUCER_MODULE_NAME,
  initialState,
  reducers: {
    clean() {
      return {
        ...initialState,
      };
    },
    cleanMessage() {
      return {
        ...initialState,
        message: "",
        error: "",
      };
    },
  },
  extraReducers: {
    [grid_activos.pending.type]: loadingReducer,
    [grid_inactivos.pending.type]: loadingReducer,
    [getById.pending.type]: loadingReducer,
    [form.pending.type]: loadingReducer,
    [dashboard.pending.type]: loadingReducer,
    [get_stock_v1.pending.type]: loadingReducer,
    [submit.pending.type]: loadingReducer,
    [submitStockV1.pending.type]: loadingReducer,
    [select_by_category.pending.type]: loadingReducer,
    //[check_characteristic_values_select.pending.type]: loadingReducer,

    [grid_activos.fulfilled.type]: (state, action) => {
      return {
        ...state,
        loading: false,
        error: "",
        grid: {
          ...state.grid,
          activos: {
            ...state.grid.activos,
            page: action.payload.page,
            skip: action.payload.skip,
            take: action.payload.take,
            rows: action.payload.grid_data.rows,
            data: adapter.from_api_to_grid(action.payload.grid_data.data),
            //data: action.payload.grid_data.data
          },
        },
      };
    },
    [grid_inactivos.fulfilled.type]: (state, action) => {
      return {
        ...state,
        loading: false,
        error: "",
        grid: {
          ...state.grid,
          inactivos: {
            ...state.grid.inactivos,
            page: action.payload.page,
            skip: action.payload.skip,
            take: action.payload.take,
            rows: action.payload.grid_data.rows,
            data: adapter.from_api_to_grid(action.payload.grid_data.data),
            //data: action.payload.grid_data.data
          },
        },
      };
    },
    [get_stock_v1.fulfilled.type]: (state, action) => {
      return {
        ...state,
        loading: false,
        stock: action.payload,
      };
    },
    [select_by_category.fulfilled.type]: (state, action) => {
      return {
        ...state,
        loading: false,
        select: action.payload.select,
        products: action.payload.products,
      };
    },
    [getById.fulfilled.type]: (state, action) => {
      return {
        ...state,
        loading: false,
        data: adapter.from_api_to_form(action.payload.entity),
        data_not_adapted: action.payload.entity,
      };
    },
    [dashboard.fulfilled.type]: (state, action) => {
      return {
        ...state,
        loading: false,
        dashboard: action.payload.dashboard,
      };
    },
    [form.fulfilled.type]: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
    [submit.fulfilled.type]: (state, action) => {
      return {
        ...state,
        loading: false,
        message: action.payload.message,
        formIsReady: !!action.payload.message,
        error: action.payload.error,
      };
    },
    [submitStockV1.fulfilled.type]: (state, action) => {
      return {
        ...state,
        loading: false,
        message: action.payload.message,
        formIsReady: !!action.payload.message,
        error: action.payload.error,
      };
    },
    [check_characteristic_values_select.fulfilled.type]: (state, action) => {
      return state;
      /*return {
                ...state,
                loading: false
            }*/
    },
  },
});

export const { clean, cleanMessage } = slice.actions;

export default slice.reducer;
